import { useFormContext } from "react-hook-form";
import type { ReactNode } from "react";
import type { Country } from "react-phone-number-input";

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import { PhoneInput } from "@patient-watch/ui/phone-input";
/* oxlint-disable jsx-a11y/no-autofocus -- invite/signup forms focus the phone field intentionally */
import {
    localeToPhoneCountry,
    localeToPhoneLabels,
    localeToPhoneUiLocale,
    normaliseLocaleTag,
} from "./localePhoneCountry";
import type { LooseRHFField } from "./utils/loose-rhf-field";

interface Props {
    name?: string;
    label?: ReactNode;
    tabIndex?: number;
    autoFocus?: boolean;
    className?: string;
    disabled?: boolean;
    labelAction?: ReactNode;
    /** Explicit default country (overrides locale). */
    defaultCountry?: Country;
    /** App or browser locale used to pick the default country flag/dial code. */
    locale?: string | null;
    placeholder?: string;
}

function resolveDefaultPhoneCountry(locale?: string | null, defaultCountry?: Country): Country {
    if (defaultCountry) {
        return defaultCountry;
    }

    if (locale) {
        return localeToPhoneCountry(locale);
    }

    if (typeof navigator !== "undefined" && navigator.language) {
        return localeToPhoneCountry(navigator.language);
    }

    return "GB";
}

const Phone: React.FC<Props> = ({
    className,
    name = "phone",
    label = "Phone Number",
    autoFocus = true,
    labelAction,
    tabIndex,
    disabled,
    defaultCountry,
    locale,
    placeholder = "Enter phone number",
}: Props) => {
    const { control } = useFormContext();
    const resolvedDefaultCountry = resolveDefaultPhoneCountry(locale, defaultCountry);
    const labels = localeToPhoneLabels(locale);
    const phoneUiLocale = localeToPhoneUiLocale(locale);
    const localeKey = normaliseLocaleTag(locale) ?? locale ?? "en-GB";

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }: { field: LooseRHFField }) => (
                <FormItem className={className}>
                    {labelAction ? (
                        <div className="flex flex-row justify-between items-center gap-2">
                            <FormLabel className="mb-0">{label}</FormLabel>
                            <div className="shrink-0">{labelAction}</div>
                        </div>
                    ) : (
                        <FormLabel>{label}</FormLabel>
                    )}
                    <FormControl>
                        <PhoneInput
                            key={`${localeKey}-${resolvedDefaultCountry}`}
                            placeholder={placeholder}
                            labels={labels}
                            locales={phoneUiLocale}
                            value={typeof field.value === "string" ? field.value : undefined}
                            onChange={field.onChange}
                            autoComplete="tel"
                            defaultCountry={resolvedDefaultCountry}
                            international
                            autoFocus={autoFocus}
                            tabIndex={tabIndex}
                            disabled={disabled}
                            countrySelectProps={{
                                "aria-label": labels.country ?? "Select country",
                            }}
                        />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
};

export default Phone;
